import type { LegacyTouchState } from '../services/LegacyEnterpriseServer/types'
import type { LegacyExecuteRequest, LegacyExecution, LegacyPreflightResult, LegacyRegionMode } from '../services/LegacyEnterpriseServer/ownerTypes'
import { legacyDeadlineLabel } from './legacyEnterpriseTouch'

export const LEGACY_OWNER_REGION_OPTIONS: Array<{
  value: LegacyRegionMode
  title: string
  subtitle: string
}> = [
  { value: 'VIP_ONLY', title: '中国大陆', subtitle: '全体成员在中国使用。企业空间与剩余字符归属中国版（doclingo.vip）' },
  { value: 'AI_ONLY', title: '国际（不包括中国大陆）', subtitle: '全体成员在中国以外地区使用。企业空间与剩余字符归属国际版（doclingo.ai）' },
  { value: 'CROSS_REGION', title: '全球跨区合作（中国大陆+国际）', subtitle: '成员分布于中国与其他地区。开通中国+国际双企业空间，剩余字符按比例分配至两个额度池' },
]

const MAX_LONG = BigInt('9223372036854775807')
const WAN = BigInt(10000)
const pendingStatuses = new Set(['WAITING_DRAIN', 'TARGET_PREPARING', 'TARGET_COMMITTING', 'FINALIZING', 'MIGRATING', 'PENDING'])
const statusI18nKeys: Record<string, string> = {
  WAITING_DRAIN: 'legacyOwner_status_waiting_drain',
  TARGET_PREPARING: 'legacyOwner_status_target_preparing',
  TARGET_COMMITTING: 'legacyOwner_status_target_committing',
  FINALIZING: 'legacyOwner_status_finalizing',
  COMPLETED: 'legacyOwner_status_completed',
  SOURCE_CHANGED: 'legacyOwner_status_source_changed',
  MIGRATING: 'legacyOwner_status_migrating',
  PENDING: 'legacyOwner_status_pending',
  PRECHECK: 'legacyOwner_status_precheck',
  DEFERRED: 'legacyOwner_status_deferred',
  DEFAULTED: 'legacyOwner_status_defaulted',
}

export function legacyOwnerQuota(value: unknown): string {
  if (typeof value === 'number' && !Number.isSafeInteger(value)) throw new Error('字符数必须使用精确的整数字符串')
  const text = String(value ?? '').trim()
  if (!/^\d+$/.test(text)) throw new Error('字符数必须是非负整数')
  const parsed = BigInt(text)
  if (parsed > MAX_LONG) throw new Error('字符数超出支持范围')
  return parsed.toString()
}

export function legacyOwnerTotalWords(state?: Pick<LegacyTouchState, 'totalWords' | 'serviceWords' | 'fuelWords'> | null): string {
  if (!state) throw new Error('缺少企业字符总数')
  if (state.totalWords !== undefined && state.totalWords !== null && state.totalWords !== '') return legacyOwnerQuota(state.totalWords)
  if ((state.serviceWords === undefined || state.serviceWords === null || state.serviceWords === '')
    && (state.fuelWords === undefined || state.fuelWords === null || state.fuelWords === '')) throw new Error('缺少企业字符总数')
  const total = BigInt(legacyOwnerQuota(state.serviceWords || '0')) + BigInt(legacyOwnerQuota(state.fuelWords || '0'))
  if (total > MAX_LONG) throw new Error('字符数超出支持范围')
  return total.toString()
}

export function defaultLegacyOwnerAllocation(total: unknown): { vipQuota: string; aiQuota: string } {
  const value = BigInt(legacyOwnerQuota(total))
  const ai = value / BigInt(2)
  return { aiQuota: ai.toString(), vipQuota: (value - ai).toString() }
}

export function validateLegacyOwnerAllocation(total: unknown, vipQuota: unknown): { vipQuota: string; aiQuota: string } {
  const totalWords = BigInt(legacyOwnerQuota(total))
  const vipWords = BigInt(legacyOwnerQuota(vipQuota))
  if (vipWords > totalWords) throw new Error('中国区字符不能超过总字符')
  if (totalWords > BigInt(0) && (vipWords === BigInt(0) || vipWords === totalWords)) {
    throw new Error('剩余字符大于 0 时，中国区和国际区都必须大于 0')
  }
  return { vipQuota: vipWords.toString(), aiQuota: (totalWords - vipWords).toString() }
}

export function quotaToWanInput(value: unknown): string {
  const words = BigInt(legacyOwnerQuota(value))
  const integer = words / WAN
  const fraction = words % WAN
  if (fraction === BigInt(0)) return integer.toString()
  return `${integer}.${fraction.toString().padStart(4, '0').replace(/0+$/, '')}`
}

export function wanInputToQuota(value: unknown): string {
  const text = String(value ?? '').trim()
  if (!/^\d+(?:\.\d{0,4})?$/.test(text)) throw new Error('请输入最多 4 位小数的万字符')
  const [whole, decimal = ''] = text.split('.')
  return legacyOwnerQuota((BigInt(whole) * WAN + BigInt(decimal.padEnd(4, '0'))).toString())
}

export function formatLegacyOwnerWords(value: unknown): string {
  try { return BigInt(legacyOwnerQuota(value)).toLocaleString('zh-CN') } catch { return '—' }
}

export function legacyOwnerDeadlineText(state?: Pick<LegacyTouchState, 'deadline'> | null): string {
  const label = legacyDeadlineLabel(state?.deadline)
  return label ? `${label} 前` : ''
}

export function requiresLegacyPreflight(mode: LegacyRegionMode): boolean {
  return mode !== 'AI_ONLY'
}

export function isLegacyPreflightBlocking(preflight?: Pick<LegacyPreflightResult, 'action'> | null): boolean {
  return preflight?.action === 'MANUAL_REQUIRED'
}

export function requiresLegacyLeaveConfirm(preflight?: Pick<LegacyPreflightResult, 'action'> | null): boolean {
  return preflight?.action === 'LEAVE_CONFIRM_REQUIRED'
}

export function canExecuteLegacyOwnerMigration(args: {
  mode: LegacyRegionMode
  totalWords?: string
  vipQuota: string
  bindRequired?: boolean
  preflight?: Pick<LegacyPreflightResult, 'action' | 'preflightToken'> | null
  confirmedLeave?: boolean
}): boolean {
  if (args.bindRequired) return false
  if (args.mode === 'CROSS_REGION' && !args.totalWords) return false
  if (args.mode === 'CROSS_REGION') {
    try { validateLegacyOwnerAllocation(args.totalWords!, args.vipQuota) } catch { return false }
  }
  if (!requiresLegacyPreflight(args.mode)) return true
  if (!args.preflight?.preflightToken || isLegacyPreflightBlocking(args.preflight)) return false
  if (requiresLegacyLeaveConfirm(args.preflight) && !args.confirmedLeave) return false
  return true
}

export function isLegacyExecutionPending(execution?: Pick<LegacyExecution, 'status' | 'manualRequired'> | null): boolean {
  if (!execution?.status || execution.manualRequired) return false
  return pendingStatuses.has(execution.status)
}

export function isLegacyExecutionCompleted(execution?: Pick<LegacyExecution, 'status'> | null, state?: Pick<LegacyTouchState, 'status'> | null): boolean {
  return execution?.status === 'COMPLETED' || state?.status === 'COMPLETED'
}

export function legacyOwnerStatusI18nKey(status: unknown): string {
  const normalized = String(status || '').trim().toUpperCase()
  return statusI18nKeys[normalized] || 'legacyOwner_status_unknown'
}

export function legacyOwnerRequestId(): string {
  const cryptoApi = globalThis.crypto
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID()
  return `legacy-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`
}

export function freezeLegacyExecuteBody(body: LegacyExecuteRequest): Readonly<LegacyExecuteRequest> {
  return Object.freeze({
    migrationNo: body.migrationNo,
    requestId: body.requestId,
    regionMode: body.regionMode,
    vipQuota: body.vipQuota,
    preflightToken: body.preflightToken,
    confirmedLeave: body.confirmedLeave,
  })
}

const executionDrafts = new Map<string, Readonly<LegacyExecuteRequest>>()

export function rememberLegacyExecuteBody(key: string, body: Readonly<LegacyExecuteRequest>): void {
  if (key) executionDrafts.set(key, body)
}

export function getRememberedLegacyExecuteBody(key?: string): Readonly<LegacyExecuteRequest> | null {
  return key ? executionDrafts.get(key) || null : null
}

export function forgetLegacyExecuteBody(key?: string): void {
  if (key) executionDrafts.delete(key)
}
